import React from 'react';
import dayjs from 'dayjs';
import { DatePicker, TimePicker } from 'antd';
import enUS from 'antd/es/locale/en_US';
import arEG from 'antd/es/locale/ar_EG';
import 'dayjs/locale/ar';
import { useCrmLocale } from '../../ui/locale/crmLocale';
import './DateTimeInput.css';

export interface DateTimeInputProps {
  language?: 'en' | 'ar';
  type?: 'date' | 'month' | 'time' | 'datetime-local';
  value?: string;
  onChange?: (value: string) => void;
  min?: string;
  max?: string;
  readOnly?: boolean;
  disabled?: boolean;
  required?: boolean;
  id?: string;
  name?: string;
  className?: string;
  style?: React.CSSProperties;
  placeholder?: string;
  'aria-label'?: string;
  popupZIndex?: number;
  popupClassName?: string;
}

/** Localized controls retain the existing ISO date/month and HH:mm form values. */
export const DateTimeInput: React.FC<DateTimeInputProps> = ({ type = 'date', language: languageOverride, value, onChange, min, max, readOnly, className, placeholder, popupZIndex, popupClassName, ...rest }) => {
  const { language: activeLanguage } = useCrmLocale();
  const language = languageOverride ?? activeLanguage;
  const defaultPlaceholder = language === 'ar'
    ? type === 'time' ? 'اختار الوقت' : type === 'month' ? 'اختار الشهر' : 'اختار التاريخ'
    : type === 'time' ? 'Choose time' : type === 'month' ? 'Choose month' : 'Choose date';
  const [touchPicker, setTouchPicker] = React.useState(() => typeof window !== 'undefined'
    && window.matchMedia('(max-width: 767px), (pointer: coarse)').matches);
  React.useEffect(() => {
    const query = window.matchMedia('(max-width: 767px), (pointer: coarse)');
    const update = () => setTouchPicker(query.matches);
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);
  const locale = language === 'ar' ? arEG : enUS;
  const format = type === 'time' ? 'HH:mm' : type === 'month' ? 'YYYY-MM' : type === 'datetime-local' ? 'YYYY-MM-DDTHH:mm' : 'YYYY-MM-DD';
  const parsed = value ? dayjs(type === 'time' ? `2000-01-01T${value}` : value) : null;
  const commitTypedValue = (event: React.FocusEvent<HTMLElement>) => {
    if (readOnly || touchPicker || rest.disabled || !(event.target instanceof HTMLInputElement)) return;
    const text = event.target.value;
    const candidate = dayjs(type === 'time' ? `2000-01-01T${text}` : text);
    // Preserve manual range validation in the original forms, including past-date warnings.
    if (text !== value && candidate.isValid() && candidate.format(format) === text) onChange?.(text);
  };
  const common = {
    ...rest,
    className: ['crm-temporal-input', className].filter(Boolean).join(' '),
    value: parsed?.isValid() ? parsed.locale(language === 'ar' ? 'ar' : 'en') : null,
    format,
    placeholder: placeholder ?? defaultPlaceholder,
    // Suppress the mobile text keyboard without disabling calendar/time selection.
    inputReadOnly: Boolean(readOnly || touchPicker),
    open: readOnly ? false : undefined,
    allowClear: !readOnly,
    getPopupContainer: () => document.body,
    popupAlign: {offset: touchPicker ? [0, 0] : undefined, overflow: {adjustX: true, adjustY: true, shiftX: true, shiftY: true}},
    styles: popupZIndex === undefined ? undefined : {popup: {root: {zIndex: popupZIndex}}},
    classNames: {popup: {root: ['crm-temporal-popup', popupClassName].filter(Boolean).join(' ')}},
    onChange: (next: dayjs.Dayjs | null) => onChange?.(next ? next.format(format) : ''),
    onBlur: commitTypedValue,
  };
  if (type === 'time') {
    const range = (count: number) => Array.from({ length: count }, (_, index) => index);
    const [minHour, minMinute] = (min ?? '').split(':').map(Number);
    const [maxHour, maxMinute] = (max ?? '').split(':').map(Number);
    return <TimePicker {...common} locale={locale.DatePicker} needConfirm={false} disabledTime={() => ({
      disabledHours: () => range(24).filter(hour => (Boolean(min) && hour < minHour) || (Boolean(max) && hour > maxHour)),
      disabledMinutes: hour => range(60).filter(minute => (Boolean(min) && hour === minHour && minute < minMinute) || (Boolean(max) && hour === maxHour && minute > maxMinute)),
    })} />;
  }
  return <DatePicker {...common} showTime={type === 'datetime-local' ? {format: 'HH:mm'} : false} locale={locale.DatePicker} picker={type === 'month' ? 'month' : 'date'} minDate={min ? dayjs(min) : undefined} maxDate={max ? dayjs(max) : undefined} />;
};

export default DateTimeInput;
